/**
 * Feature flags d'exposition UI lus depuis la table public.feature_flags
 * (migration 0096).
 *
 * Différent de exercises.content_version — ce dernier est la source de vérité
 * "cet exo est v1 ou v2", géré par exo. Les feature flags ici contrôlent
 * uniquement la VISIBILITÉ DES BOUTONS / ROUTES côté tuteur (ex: cacher
 * /edit-v2 tant que la beta n'est pas ouverte).
 *
 * Évaluation : enabled=true → actif pour tous. Sinon actif si :
 *   - role utilisateur ∈ allowed_roles, OU
 *   - uid utilisateur ∈ allowed_user_ids.
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';

export type FeatureFlagKey =
  | 'exercise_v2_editor'
  | 'exercise_v2_player'
  | 'pipeline_v2_default'
  | 'lesson_v2_editor'
  | 'paper_player';

interface FeatureFlagRow {
  key: string;
  enabled: boolean;
  allowed_roles: string[];
  allowed_user_ids: string[];
}

/** Charge tous les flags en un coup (table petite). */
export function useFeatureFlags() {
  return useQuery({
    queryKey: ['feature-flags'],
    staleTime: 5 * 60 * 1000, // 5 min — les flags bougent peu
    queryFn: async (): Promise<Record<string, FeatureFlagRow>> => {
      // Cast : la table feature_flags (migration 0096) n'est pas encore dans
      // types.generated.ts — régénérer via `pnpm db:gen:types` après déploiement.
      const { data, error } = await (
        supabase as unknown as {
          from: (t: string) => {
            select: (
              cols: string,
            ) => Promise<{ data: FeatureFlagRow[] | null; error: Error | null }>;
          };
        }
      )
        .from('feature_flags')
        .select('key, enabled, allowed_roles, allowed_user_ids');
      if (error) throw error;
      const map: Record<string, FeatureFlagRow> = {};
      for (const row of data ?? []) map[row.key] = row;
      return map;
    },
  });
}

/**
 * Évalue un flag pour le user courant. Renvoie `false` tant que les flags
 * et le profile ne sont pas chargés (fail-closed).
 */
export function useFeatureFlag(
  key: FeatureFlagKey,
  user: { id?: string | null; role?: string | null } | null | undefined,
): boolean {
  const { data: flags } = useFeatureFlags();
  if (!flags || !user) return false;
  const row = flags[key];
  if (!row) return false;
  if (row.enabled) return true;
  if (user.role && row.allowed_roles.includes(user.role)) return true;
  if (user.id && row.allowed_user_ids.includes(user.id)) return true;
  return false;
}
